import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLakes, setSearchParams, setSortConfig, setSelectedLake, fetchLakeSpots, clearSearch } from '../../store/slices/lakesSlice';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TableSortLabel,
  Card,
  CardContent,
  Grid,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Chip,
  Checkbox,
  ListItemText,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { ref, getBytes } from 'firebase/storage';
import { kml } from '@tmcw/togeojson';
import { storage } from '../../firebase';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 46.03978,
  lng: -89.62341
};

const libraries = ['drawing'];

const lakeTypes = [
  "CDR",
  "CNL",
  "FLR",
  "GLC",
  "HLR",
  "MTL",
  "RVS",
  "SNL",
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function Lakes() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timerRef = useRef(null);

  const {
    lakes,
    searchParams,
    sortConfig,
    selectedLake,
    lakeSpots
  } = useSelector((state) => state.lakes);

  const [hoveredSpot, setHoveredSpot] = useState(null);

  useEffect(() => {
    dispatch(fetchLakes(searchParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const executeSearch = (currentParams) => {
    dispatch(fetchLakes(currentParams));
  };

  const handleParamChange = (field, value) => {
    const newParams = { ...searchParams, [field]: value };
    dispatch(setSearchParams({ [field]: value }));

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      executeSearch(newParams);
    }, 500);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      executeSearch(searchParams);
    }
  };

  const handleRequestSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    dispatch(setSortConfig({ key, direction }));
  };

  const handleRowClick = (lake) => {
    dispatch(setSelectedLake(lake));
    dispatch(fetchLakeSpots(lake.id));
  };

  const handleEditClick = (e, lake) => {
    e.stopPropagation();
    navigate(`/lakes/${lake.id}`);
  };

  const handleAddLake = () => {
    navigate('/lakes/new');
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries
  });

  const [map, setMap] = useState(null);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Initialize center and zoom based on current selectedLake (if any) on mount
  const [initialViewState] = useState(() => {
    if (selectedLake) {
      return {
        center: { lat: selectedLake.latitude, lng: selectedLake.longitude },
        zoom: 12
      };
    }
    return {
      center: defaultCenter,
      zoom: 8
    };
  });

  useEffect(() => {
    if (map) {
      if (selectedLake) {
        map.panTo({ lat: selectedLake.latitude, lng: selectedLake.longitude });
        map.setZoom(12);
      } else {
        map.panTo(defaultCenter);
        map.setZoom(8);
      }
    }
  }, [selectedLake, map]);

  // Add GeoJSON data to map when lake is selected
  useEffect(() => {
    const loadAndDisplayGeoData = async () => {
      if (!map || !selectedLake) {
        if (map) {
          // Clear features when no lake is selected
          map.data.forEach((feature) => {
            map.data.remove(feature);
          });
        }
        return;
      }

      // Clear existing features
      map.data.forEach((feature) => {
        map.data.remove(feature);
      });

      // Priority 1: Load from KML file if it exists
      if (selectedLake.kmlFile) {
        try {
          // Fetch KML file from storage using Firebase SDK
          const storageRef = ref(storage, selectedLake.kmlFile);
          const bytes = await getBytes(storageRef);
          const kmlText = new TextDecoder().decode(bytes);
          
          // Parse KML to GeoJSON
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
          const geoJson = kml(kmlDoc);

          // Add GeoJSON to map
          map.data.addGeoJson(geoJson);

          // Style the data layer
          map.data.setStyle((feature) => {
            const properties = feature.getProperty('properties') || {};
            return {
              fillColor: properties.fillColor || '#2196F3',
              fillOpacity: properties.fillOpacity !== undefined ? properties.fillOpacity : 0.4,
              strokeColor: properties.strokeColor || '#2196F3',
              strokeOpacity: properties.strokeOpacity !== undefined ? properties.strokeOpacity : 0.8,
              strokeWeight: properties.strokeWeight !== undefined ? properties.strokeWeight : 2,
            };
          });
        } catch (e) {
          console.error('Error loading KML file:', e);
        }
      }
      // Priority 2: Load from manually drawn geoJson if no KML file
      else if (selectedLake.geoJson) {
        try {
          // Parse and add GeoJSON
          const geoJson = JSON.parse(selectedLake.geoJson);
          map.data.addGeoJson(geoJson);

          // Style the data layer
          map.data.setStyle((feature) => {
            const properties = feature.getProperty('properties') || {};
            return {
              fillColor: properties.fillColor || '#2196F3',
              fillOpacity: properties.fillOpacity !== undefined ? properties.fillOpacity : 0.4,
              strokeColor: properties.strokeColor || '#2196F3',
              strokeOpacity: properties.strokeOpacity !== undefined ? properties.strokeOpacity : 0.8,
              strokeWeight: properties.strokeWeight !== undefined ? properties.strokeWeight : 2,
            };
          });
        } catch (e) {
          console.error('Error adding manually drawn geoJson to map:', e);
        }
      }
    };

    loadAndDisplayGeoData();

    // Cleanup function
    return () => {
      if (map) {
        map.data.forEach((feature) => {
          map.data.remove(feature);
        });
      }
    };
  }, [map, selectedLake]);

  return (
    <Box sx={{ 
      p: 3, 
      height: 'calc(100vh - 64px)', 
      display: 'flex', 
      flexDirection: 'column',
      boxSizing: 'border-box'
    }}>
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Name"
            variant="outlined"
            value={searchParams.name}
            onChange={(e) => handleParamChange('name', e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
          />
          <TextField
            label="Station ID"
            variant="outlined"
            value={searchParams.stationId}
            onChange={(e) => handleParamChange('stationId', e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
          />
          <FormControl sx={{ width: 300 }} size="small">
            <InputLabel id="lake-type-label">Lake Type</InputLabel>
            <Select
              labelId="lake-type-label"
              id="lake-type-select"
              multiple
              value={Array.isArray(searchParams.lakeType) ? searchParams.lakeType : []}
              onChange={(e) => handleParamChange('lakeType', e.target.value)}
              input={<OutlinedInput label="Lake Type" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {lakeTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={Array.isArray(searchParams.lakeType) && searchParams.lakeType.indexOf(type) > -1} />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: 120 }} size="small">
            <InputLabel id="published-label">Published</InputLabel>
            <Select
              labelId="published-label"
              id="published-select"
              value={searchParams.published || 'all'}
              label="Published"
              onChange={(e) => handleParamChange('published', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddLake}
            sx={{ ml: 'auto' }}
          >
            Add Lake
          </Button>
        </Box>
      </Paper>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        {/* Results Table */}
        <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'name'}
                      direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                      onClick={() => handleRequestSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'stationId'}
                      direction={sortConfig.key === 'stationId' ? sortConfig.direction : 'asc'}
                      onClick={() => handleRequestSort('stationId')}
                    >
                      Station ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'lakeType'}
                      direction={sortConfig.key === 'lakeType' ? sortConfig.direction : 'asc'}
                      onClick={() => handleRequestSort('lakeType')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'acres'}
                      direction={sortConfig.key === 'acres' ? sortConfig.direction : 'asc'}
                      onClick={() => handleRequestSort('acres')}
                    >
                      Acres
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'published'}
                      direction={sortConfig.key === 'published' ? sortConfig.direction : 'asc'}
                      onClick={() => handleRequestSort('published')}
                    >
                      Published
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Has KML</TableCell>
                  <TableCell>Has GeoJSON</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lakes.map((lake) => (
                  <TableRow 
                    key={lake.id} 
                    hover 
                    onClick={() => handleRowClick(lake)}
                    selected={selectedLake?.id === lake.id}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{lake.name}</TableCell>
                    <TableCell>{lake.stationId}</TableCell>
                    <TableCell>{lake.lakeType}</TableCell>
                    <TableCell>{lake.acres}</TableCell>
                    <TableCell>{lake.published ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {lake.kmlFile ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {lake.geoJson ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleEditClick(e, lake)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {lakes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No lakes found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Map Section */}
        <Card sx={{ flex: 1, minHeight: 0 }}>
          <CardContent sx={{ height: '100%', p: '0 !important' }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={initialViewState.center}
                zoom={initialViewState.zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
              >
                {selectedLake && (
                  <>
                    <Marker
                      position={{ lat: selectedLake.latitude, lng: selectedLake.longitude }}
                      title={selectedLake.name}
                    />
                    {lakeSpots.map((spot) => (
                      <Marker
                        key={spot.id}
                        position={{ lat: spot.latitude, lng: spot.longitude }}
                        icon={{
                          url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                        }}
                        onMouseOver={() => setHoveredSpot(spot)}
                        onMouseOut={() => setHoveredSpot(null)}
                      />
                    ))}
                    {hoveredSpot && (
                      <InfoWindow
                        position={{ lat: hoveredSpot.latitude, lng: hoveredSpot.longitude }}
                        onCloseClick={() => setHoveredSpot(null)}
                        options={{ 
                          disableAutoPan: true,
                          pixelOffset: new window.google.maps.Size(0, -30)
                        }}
                      >
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {hoveredSpot.name}
                          </Typography>
                          {hoveredSpot.description && (
                            <Typography variant="body2">
                              {hoveredSpot.description}
                            </Typography>
                          )}
                        </Box>
                      </InfoWindow>
                    )}
                  </>
                )}
              </GoogleMap>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>Loading Map...</Typography>
              </Box>
            )}
            {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
              <Typography color="error" variant="caption" sx={{ p: 1, display: 'block' }}>
                Note: Google Maps API Key is missing. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Lakes;
